import { Box, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import type { AlertParams } from '@/contexts/AlertContext'
import { useAlertContext } from '@/contexts/AlertContext'
import type { ActivationGroup } from '@/features/activationGroup/types'
import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import { validateConflicts } from '@/features/bidding/endpoints/bids'
import type { ApiValidateConflictsPayload, ApiValidateConflictsResponse } from '@/features/bidding/types/api'

class ValidationConflictError extends Error {
  public readonly isValidationConflict = true
  public readonly conflictMessages: string

  constructor(message: string, conflictMessages: string) {
    super(message)
    this.name = 'ValidationConflictError'
    this.conflictMessages = conflictMessages
  }
}

type UseValidateRegulationObjectParams = {
  eligibleActivationGroups: ActivationGroup[]
  onAcceptConflicts?: () => void
}

function showWarningWithActions(
  pushAlert: (params: Omit<AlertParams, 'uuid'>) => void,
  messages: string,
  handleFixConflicts: () => void,
  t: TFunction<'translation', undefined>,
  handleAcceptConflicts: () => void,
) {
  pushAlert({
    message: (
      <Box>
        <Box sx={{ whiteSpace: 'pre-line', mb: 2, maxHeight: '50vh', overflowY: 'auto', pr: 2 }}>{messages}</Box>
        <Stack direction="row" gap={1} justifyContent="flex-start">
          <CustomButton color="inherit" size="small" variant="contained" onClick={handleFixConflicts}>
            {t('bidding.validation.regulation_object.warning.fix_button')}
          </CustomButton>
          <CustomButton color="inherit" size="small" variant="outlined" onClick={handleAcceptConflicts}>
            {t('bidding.validation.regulation_object.warning.accept_button')}
          </CustomButton>
        </Stack>
      </Box>
    ),
    severity: 'warning',
    title: t('bidding.validation.regulation_object.warning.title'),
    isPersistent: true,
  })
}

function formatConflictMessages(
  data: ApiValidateConflictsResponse,
  activationGroupCodeByUuid: Map<string, string>,
  t: TFunction<'translation', undefined>,
) {
  return data.issues
    .flatMap((bidConflict) =>
      bidConflict.conflicts.map((conflict) => {
        // Parse ISO strings to DateTime objects
        const start = DateTime.fromISO(conflict.ptu.start.toString())
        const end = DateTime.fromISO(conflict.ptu.end.toString())

        // show the start/end times in market time, not user local time
        const startTime = start.setZone(MARKET_TIMEZONE).toFormat('HH:mm')
        const endTime = end.setZone(MARKET_TIMEZONE).toFormat('HH:mm')

        // Look up the activation group code from the UUID
        const activationGroupCode =
          activationGroupCodeByUuid.get(bidConflict.activationGroupUuid) ?? bidConflict.activationGroupUuid

        return t('bidding.validation.regulation_object.conflict_message', {
          activationGroupCode,
          conflictingActivationGroupCode: conflict.conflictingActivationGroupCode,
          startTime,
          endTime,
        })
      }),
    )
    .join('\n')
}

function formatMessagesAndShowDialog(
  pushAlert: (params: Omit<AlertParams, 'uuid'>) => void,
  t: TFunction<'translation', undefined>,
  eligibleActivationGroups: ActivationGroup[],
  data: ApiValidateConflictsResponse,
  resetAlerts: () => void,
  onAcceptConflicts?: () => void,
) {
  // Create a map from activation group UUID to code for quick lookup
  const activationGroupCodeByUuid = new Map(eligibleActivationGroups.map((ag) => [ag.uuid, ag.code]))

  // Format conflict messages (one per line)
  const messages = formatConflictMessages(data, activationGroupCodeByUuid, t)

  // Show warning alert with action buttons below the message
  const handleAcceptConflicts = () => {
    resetAlerts()
    onAcceptConflicts?.()
  }
  const handleFixConflicts = () => {
    resetAlerts()
  }
  showWarningWithActions(pushAlert, messages, handleFixConflicts, t, handleAcceptConflicts)
  return messages
}

export function useValidateRegulationObject({
  eligibleActivationGroups,
  onAcceptConflicts,
}: UseValidateRegulationObjectParams) {
  const { t } = useTranslation()
  const { pushAlert, resetAlerts } = useAlertContext()

  const mutation = useMutation({
    mutationFn: async (payload: ApiValidateConflictsPayload) => {
      const data = await validateConflicts(payload)

      // Check if validation failed (valid: false)
      if (!data.valid && data.issues.length > 0) {
        const messages = formatMessagesAndShowDialog(
          pushAlert,
          t,
          eligibleActivationGroups,
          data,
          resetAlerts,
          onAcceptConflicts,
        )

        // Throw error to prevent navigation - but it's now a warning that user can accept
        throw new ValidationConflictError('Validation failed: regulation object conflicts detected', messages)
      }

      return data
    },
    onError: (error: Error & { isValidationConflict?: boolean; conflictMessages?: string }) => {
      // Validation conflicts are now shown as warnings in mutationFn, so skip showing error alert
      if (error.isValidationConflict) {
        return
      }

      // Show generic error for other errors (API failures, network errors, etc.)
      pushAlert({
        message: t('bidding.validation.regulation_object.error.description'),
        severity: 'error',
        title: t('bidding.validation.regulation_object.error.title'),
      })
    },
  })

  return {
    validate: mutation.mutateAsync,
    isValidating: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    response: mutation.data as ApiValidateConflictsResponse | undefined,
  }
}

export function useMultimarketValidateRegulationObject({
  eligibleActivationGroups,
  onAcceptConflicts,
}: UseValidateRegulationObjectParams) {
  const { t } = useTranslation()
  const { pushAlert, resetAlerts } = useAlertContext()

  const mutation = useMutation({
    mutationFn: async (payloads: ApiValidateConflictsPayload[]) => {
      const combinedData: ApiValidateConflictsResponse = {
        valid: true,
        issues: [],
      }
      for (const payload of payloads) {
        const data = await validateConflicts(payload)

        combinedData.valid = combinedData.valid && data.valid
        combinedData.issues = combinedData.issues.concat(data.issues)
      }

      // Check if validation failed (valid: false)
      if (!combinedData.valid && combinedData.issues.length > 0) {
        const messages = formatMessagesAndShowDialog(
          pushAlert,
          t,
          eligibleActivationGroups,
          combinedData,
          resetAlerts,
          onAcceptConflicts,
        )

        // Throw error to prevent navigation - but it's now a warning that user can accept
        throw new ValidationConflictError('Validation failed: regulation object conflicts detected', messages)
      }

      return combinedData
    },
    onError: (error: Error & { isValidationConflict?: boolean; conflictMessages?: string }) => {
      // Validation conflicts are now shown as warnings in mutationFn, so skip showing error alert
      if (error.isValidationConflict) {
        return
      }

      // Show generic error for other errors (API failures, network errors, etc.)
      pushAlert({
        message: t('bidding.validation.regulation_object.error.description'),
        severity: 'error',
        title: t('bidding.validation.regulation_object.error.title'),
      })
    },
  })

  return {
    validate: mutation.mutateAsync,
    isValidating: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    response: mutation.data as ApiValidateConflictsResponse | undefined,
  }
}
